import { AccountLayout } from "@solana/spl-token0.3.8";
import { useConnection } from "@solana/wallet-adapter-react";
import type { AccountInfo, Commitment, Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { chunks } from "@stepfinance/common";
import { type NormalizedTokenAccount, type TokenAccount, TokenAccountParser } from "@stepfinance/tokens";
import type { FC } from "react";
import React, { useContext, useEffect, useMemo, useState } from "react";

import { useAccountRent } from "../hooks/accounts/accountsRent";
import type { ParsedUserTokenAccounts, UserTokenAccountsMap } from "../hooks/accounts/useUserTokenAccounts";
import { initialParsedUserTokenAccounts, useTokenAccounts } from "../hooks/accounts/useUserTokenAccounts";
import { cache } from "../utils/cache";

export interface TokenFilters {
  safe: boolean;
  unknown: boolean;
  nonAta: boolean;
}

export interface AccountsContextState {
  userTokenAccounts: NormalizedTokenAccount[];
  userTokenAccountsMap: UserTokenAccountsMap;
  parsedUserTokenAccounts: ParsedUserTokenAccounts;
  accountRent: number;
}

export type AccountsRefresher = () => void;

const AccountsContext = React.createContext<AccountsContextState>({
  userTokenAccounts: [],
  userTokenAccountsMap: {},
  parsedUserTokenAccounts: initialParsedUserTokenAccounts,
  accountRent: 0,
});

const AccountsContextRefresher = React.createContext<AccountsRefresher>(() => {});

export const AccountsProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { userTokenAccounts, userTokenAccountsMap, parsedUserTokenAccounts, fetchTokenAccounts } = useTokenAccounts();
  const { totalRent: accountRent } = useAccountRent(
    userTokenAccounts.filter((acc) => acc.tokenAmount.uiAmount <= 0 && !acc.filters.unknown),
    AccountLayout.span,
  );

  const contextValue = useMemo(
    () => ({
      userTokenAccounts,
      userTokenAccountsMap,
      accountRent,
      parsedUserTokenAccounts,
    }),
    [userTokenAccounts, accountRent, parsedUserTokenAccounts, userTokenAccountsMap],
  );

  return (
    <AccountsContext.Provider value={contextValue}>
      <AccountsContextRefresher.Provider value={fetchTokenAccounts}>{children}</AccountsContextRefresher.Provider>
    </AccountsContext.Provider>
  );
};

export const useAccountsContext = () => {
  return useContext(AccountsContext);
};

export const useAccountsContextRefresher = () => {
  return useContext(AccountsContextRefresher);
};

// TODO: Move all functions below to its proper file

export async function getFilteredProgramAccounts(
  connection: Connection,
  programId: PublicKey,
  filters: any,
): Promise<{ publicKey: PublicKey; accountInfo: AccountInfo<Buffer> }[]> {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const resp = await connection._rpcRequest("getProgramAccounts", [
    programId.toBase58(),
    {
      commitment: connection.commitment,
      filters,
      encoding: "base64",
    },
  ]);
  if (resp.error) {
    throw new Error(resp.error.message);
  }
  return resp.result.map(({ pubkey, account: { data, executable, owner, lamports } }) => ({
    publicKey: new PublicKey(pubkey),
    accountInfo: {
      data: Buffer.from(data[0], "base64"),
      executable,
      owner: new PublicKey(owner),
      lamports,
    },
  }));
}

export const getMultipleAccounts = async (
  connection: Connection,
  keys: string[],
  commitment: Commitment,
): Promise<{
  keys: string[];
  array: AccountInfo<Buffer>[];
}> => {
  const publicKeys = keys.map((key) => new PublicKey(key));
  const result = (
    await Promise.all(chunks(publicKeys, 99).map((chunk) => connection.getMultipleAccountsInfo(chunk, commitment)))
  ).flat();
  const keysThatExist: string[] = [];
  const array: AccountInfo<Buffer>[] = [];
  for (let i = 0; i < result.length; i++) {
    const account = result[i];
    if (account) {
      keysThatExist.push(keys[i]);
      array.push(account);
    }
  }

  return { keys: keysThatExist, array };
};

export function useAccount(pubKey?: PublicKey) {
  const { connection } = useConnection();
  const [account, setAccount] = useState<TokenAccount>();

  const key = pubKey?.toBase58();
  useEffect(() => {
    const query = async () => {
      try {
        if (!key) {
          return;
        }

        const acc = await cache
          .query(connection, key, TokenAccountParser)
          .catch((err) => console.error("AccountsProvider Error:", err));
        if (acc) {
          setAccount(acc);
        }
      } catch (err) {
        console.error(err);
      }
    };

    void query();

    const dispose = cache.emitter.onCache((e) => {
      const event = e;
      if (event.id === key) {
        void query();
      }
    });
    return () => {
      dispose();
    };
  }, [connection, key]);

  return account;
}
